import { CloudSun, X } from 'lucide-react';
import type { LayerSettings } from './LayerSettingsPanel';
import { MapDataLegends } from './MapDataLegends';
import { WeatherForecastHud } from '../weather/WeatherForecastHud';
import { WeatherConditionsWarning } from '../weather/WeatherConditionsWarning';
import type { ForecastVariable, ForecastDaySummary } from '../weather/WeatherDashboard';
import { TOUR_GRADIENT } from '../ui/TourStylePanelShell';

/** Matches TourStyleCard outer shell. */
const TOUR_CARD_SHELL =
  'overflow-hidden rounded-2xl border border-gray-200/80 bg-white shadow-2xl dark:border-gray-700/60 dark:bg-gray-900 dark:shadow-black/40';

interface MapTopRightOverlaysProps {
  layers: LayerSettings;
  isWeatherView: boolean;
  forecastDays: ForecastDaySummary[];
  forecastLoading: boolean;
  forecastVariable: ForecastVariable;
  districtName?: string | null;
  onForecastVariableChange: (variable: ForecastVariable) => void;
  onCloseWeatherView?: () => void;
  liveLocationActive?: boolean;
  unavailableMessage?: string | null;
}

/** Top-right weather HUD + map legends — product-tour card styling. */
export function MapTopRightOverlays({
  layers,
  isWeatherView,
  forecastDays,
  forecastLoading,
  forecastVariable,
  districtName,
  onForecastVariableChange,
  onCloseWeatherView,
  liveLocationActive = false,
  unavailableMessage = null,
}: MapTopRightOverlaysProps) {
  const districtLabel = districtName?.trim() || 'Your area';
  const forecastUnavailable = Boolean(unavailableMessage);

  return (
    <div
      className="pointer-events-none absolute right-3 z-[56] flex w-[calc(100vw-1.5rem)] max-w-[min(320px,calc(100vw-1.5rem))] flex-col items-end gap-2 sm:w-auto"
      style={{ top: 'calc(var(--map-top-chrome-height, 3.5rem) + 0.35rem)' }}
    >
      {isWeatherView && (
        <div className={`pointer-events-auto w-full max-w-[18rem] ${TOUR_CARD_SHELL}`}>
          <div className={`${TOUR_GRADIENT} flex items-start gap-2 px-3 py-2.5`}>
            <CloudSun size={16} className="mt-0.5 shrink-0 text-white" aria-hidden />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                <p className="text-xs font-semibold text-white">Live forecast</p>
                {liveLocationActive && (
                  <span className="relative flex h-2 w-2 shrink-0" aria-hidden>
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white/70 opacity-75" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-white" />
                  </span>
                )}
              </div>
              <p className="text-[11px] leading-snug text-white/90">
                {forecastUnavailable ? 'Forecast unavailable' : districtLabel}
              </p>
            </div>
            {onCloseWeatherView && (
              <button
                type="button"
                onClick={onCloseWeatherView}
                aria-label="Close weather forecast"
                className="shrink-0 p-0.5 text-white/70 transition-colors hover:text-white"
              >
                <X size={16} strokeWidth={2.25} />
              </button>
            )}
          </div>
          <div className="p-1.5">
            {forecastUnavailable ? (
              <WeatherConditionsWarning message={unavailableMessage!} />
            ) : (
              <WeatherForecastHud
                days={forecastDays}
                loading={forecastLoading}
                activeVariable={forecastVariable}
                onVariableChange={onForecastVariableChange}
              />
            )}
          </div>
        </div>
      )}

      <div className="pointer-events-auto flex w-full flex-col items-end gap-2">
        <MapDataLegends
          layers={layers}
          activeForecastVariable={isWeatherView ? forecastVariable : null}
          forecastDays={forecastDays}
        />
      </div>
    </div>
  );
}
